/* eslint-disable sort-imports */
import { ComboBox } from 'src/components/ui/combobox';
import { CriticalOrganizationUpdatesService } from 'src/services/critical-organization-updates.service';
import {
    CriticalOrganizationUpdatesTabs,
    UPDATE_CRITICAL_UPDATE_PATH,
} from 'src/components/critical-organization-updates/tabs';
import CriticalUpdatesTable from 'src/components/critical-organization-updates/updates-table';
import DialogModal from 'src/components/ui/dialogModal';
import { DatePicker } from 'src/components/date-picker';
import FooterPrivacy from 'src/views/auth/components/baseTemplate/FooterPrivacy';
import { HttpStatusCode } from 'src/utils/enums/http-method.enum';
import Spinner from 'src/components/ui/spinner';
import TableFooter from 'src/components/table/pagination/tableFooter';
import Notification, {
    NotificationType,
} from 'src/components/ui/notification/Notification';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    CriticalUpdateSearchType,
    ICriticalOrgUpdateItem,
    ICriticalOrgUpdatesSearchPayload,
} from 'src/services/models/critical-organization-updates.service';
import {
    CriticalUpdateSortColumn,
    CriticalUpdateSortDirection,
    sortCriticalUpdates,
} from 'src/utils/critical-organization-updates';
import { formatDateForApi, getDefaultDateRange } from 'src/utils/bulletins';

import 'src/components/critical-organization-updates/styles.scss';

const INITIAL_REQUEST_ID = 0;
const INITIAL_FILTER_RESET_KEY = 0;
const COUNTER_INCREMENT = 1;
const PAGINATION_BRAND = 'vw6';
const SCROLL_TOP = 0;

type StatusFilter = 'All' | 'Active' | 'Expired';
type LanguageFilter = 'All' | 'English' | 'French';

const ENGLISH_LANGUAGE_CODE = 1;
const FRENCH_LANGUAGE_CODE = 2;

interface IUpdateFilters {
    searchInput: string;
    statusFilter: StatusFilter;
    startDate: string;
    endDate: string;
}

const SEARCH_TYPES: Record<StatusFilter, CriticalUpdateSearchType> = {
    All: 'All',
    Active: 'ActiveDateRange',
    Expired: 'ExpiredDateRange',
};

const getDefaultFilters = (range: {
    startDate: string;
    endDate: string;
}): IUpdateFilters => ({
    searchInput: '',
    statusFilter: 'Active',
    startDate: range.startDate,
    endDate: range.endDate,
});

const buildPayload = (
    filters: IUpdateFilters,
): ICriticalOrgUpdatesSearchPayload => {
    const headline = filters.searchInput.trim();

    return {
        searchType: SEARCH_TYPES[filters.statusFilter],
        startDate: formatDateForApi(filters.startDate),
        endDate: formatDateForApi(filters.endDate),
        ...(headline ? { headline } : {}),
    };
};

const ViewCriticalOrganizationUpdates: React.FC = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const text = (key: string, defaultValue: string) =>
        t(`SystemAdmin.CriticalOrganizationUpdates.${key}`, { defaultValue });

    const [defaultDateRange] = useState(getDefaultDateRange);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [isSuccessVisible, setIsSuccessVisible] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isErrorVisible, setIsErrorVisible] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isDeleteConfirming, setIsDeleteConfirming] = useState(false);
    const [deleteTargetId, setDeleteTargetId] = useState('');
    const [dataset, setDataset] = useState<ICriticalOrgUpdateItem[]>([]);
    const [displayedContent, setDisplayedContent] = useState<
        ICriticalOrgUpdateItem[]
    >([]);
    const [searchInput, setSearchInput] = useState('');
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('Active');
    const [languageFilter, setLanguageFilter] = useState<LanguageFilter>('All');
    const [startDate, setStartDate] = useState(defaultDateRange.startDate);
    const [endDate, setEndDate] = useState(defaultDateRange.endDate);
    const [appliedFilters, setAppliedFilters] = useState<IUpdateFilters>(() =>
        getDefaultFilters(defaultDateRange),
    );
    const [filterResetKey, setFilterResetKey] = useState(
        INITIAL_FILTER_RESET_KEY,
    );
    const [sortColumn, setSortColumn] =
        useState<CriticalUpdateSortColumn>('endDate');
    const [sortDirection, setSortDirection] =
        useState<CriticalUpdateSortDirection>('asc');

    const mountedRef = useRef(false);
    const latestRequestRef = useRef(INITIAL_REQUEST_ID);

    useEffect(() => {
        const navigationState = location.state as {
            successMessage?: string;
            errorMessage?: string;
        } | null;
        const successMessage = navigationState?.successMessage;
        const errorMessage = navigationState?.errorMessage;

        if (successMessage) {
            setSuccessMessage(successMessage);
            setIsSuccessVisible(true);
        } else if (errorMessage) {
            setErrorMessage(errorMessage);
            setIsErrorVisible(true);
        }

        if (successMessage || errorMessage) {
            window.scrollTo(SCROLL_TOP, SCROLL_TOP);
            navigate(location.pathname, { replace: true, state: null });
        }
    }, []);

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
            latestRequestRef.current += COUNTER_INCREMENT;
        };
    }, []);

    const fetchUpdates = useCallback(async (filters: IUpdateFilters) => {
        const requestId = ++latestRequestRef.current;
        const isLatest = () =>
            mountedRef.current && requestId === latestRequestRef.current;

        setIsLoading(true);
        setHasError(false);
        setAppliedFilters(filters);

        try {
            const response =
                await new CriticalOrganizationUpdatesService().getCriticalOrgUpdates(
                    buildPayload(filters),
                );

            if (
                response.status !== HttpStatusCode.SUCCESS ||
                !Array.isArray(response.data)
            ) {
                throw new Error(
                    `Unable to retrieve critical organization updates. Status: ${response.status}`,
                );
            }

            if (isLatest()) {
                setDisplayedContent([]);
                setDataset(response.data as ICriticalOrgUpdateItem[]);
            }
        } catch (error) {
            if (isLatest()) {
                console.error(
                    'Unable to retrieve critical organization updates',
                    error,
                );
                setDisplayedContent([]);
                setDataset([]);
                setHasError(true);
            }
        } finally {
            if (isLatest()) {
                setIsLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        void fetchUpdates(getDefaultFilters(defaultDateRange));
    }, [fetchUpdates, defaultDateRange.startDate, defaultDateRange.endDate]);

    const statusAllLabel = text('StatusAll', 'All');
    const statusActiveLabel = text('StatusActive', 'Active');
    const statusExpiredLabel = text('StatusExpired', 'Expired');
    const languageLabel = text('ColumnLanguage', 'Language');
    const languageEnglishLabel = text('English', 'English');
    const languageFrenchLabel = text('French', 'French');

    // Read from a ref so the callbacks stay stable (DatePicker re-initializes when its handler changes).
    const latestFiltersRef = useRef<IUpdateFilters>(
        getDefaultFilters(defaultDateRange),
    );

    latestFiltersRef.current = {
        searchInput,
        statusFilter,
        startDate,
        endDate,
    };

    const applyFilters = useCallback(
        (changes: Partial<IUpdateFilters> = {}) => {
            void fetchUpdates({ ...latestFiltersRef.current, ...changes });
        },
        [fetchUpdates],
    );

    const handleStatusSelect = (value: string) => {
        const nextStatus: StatusFilter =
            value === statusActiveLabel
                ? 'Active'
                : value === statusExpiredLabel
                ? 'Expired'
                : 'All';

        if (nextStatus === statusFilter) {
            return;
        }

        setStatusFilter(nextStatus);
        applyFilters({ statusFilter: nextStatus });
    };

    const handleLanguageSelect = (value: string) => {
        const nextLanguage: LanguageFilter =
            value === languageEnglishLabel
                ? 'English'
                : value === languageFrenchLabel
                ? 'French'
                : 'All';

        setLanguageFilter(nextLanguage);
    };

    const handleDateRangeClose = useCallback(
        (nextStartDate: string, nextEndDate: string) => {
            const resolvedStartDate =
                nextStartDate || defaultDateRange.startDate;
            const resolvedEndDate = nextEndDate || defaultDateRange.endDate;

            if (
                resolvedStartDate === latestFiltersRef.current.startDate &&
                resolvedEndDate === latestFiltersRef.current.endDate
            ) {
                return;
            }

            setStartDate(resolvedStartDate);
            setEndDate(resolvedEndDate);

            if (latestFiltersRef.current.statusFilter !== 'All') {
                applyFilters({
                    startDate: resolvedStartDate,
                    endDate: resolvedEndDate,
                });
            }
        },
        [defaultDateRange.startDate, defaultDateRange.endDate, applyFilters],
    );

    const handleSearchKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>,
    ) => {
        if (event.key === 'Enter') {
            applyFilters();
        }
    };

    const clearSearchText = () => {
        setSearchInput('');
        applyFilters({ searchInput: '' });
    };

    const clearFilters = () => {
        const defaultFilters = getDefaultFilters(defaultDateRange);
        const shouldRefetch =
            appliedFilters.searchInput.trim() !== '' ||
            appliedFilters.statusFilter !== defaultFilters.statusFilter ||
            appliedFilters.startDate !== defaultFilters.startDate ||
            appliedFilters.endDate !== defaultFilters.endDate;

        setSearchInput('');
        setStatusFilter('Active');
        setLanguageFilter('All');
        setStartDate(defaultDateRange.startDate);
        setEndDate(defaultDateRange.endDate);
        setFilterResetKey((currentKey) => currentKey + COUNTER_INCREMENT);

        if (shouldRefetch) {
            void fetchUpdates(defaultFilters);
        }
    };

    const handleSort = (
        column: CriticalUpdateSortColumn,
        direction: CriticalUpdateSortDirection,
    ) => {
        setSortDirection(direction);
        setSortColumn(column);
    };

    const handleHeadlineClick = (updateId: string) => {
        navigate(UPDATE_CRITICAL_UPDATE_PATH, { state: { updateId } });
    };

    const filteredItems = useMemo(() => {
        if (languageFilter === 'All') {
            return dataset;
        }

        const languageCode =
            languageFilter === 'English'
                ? ENGLISH_LANGUAGE_CODE
                : FRENCH_LANGUAGE_CODE;

        return dataset.filter(
            (item) => Number(item.NEWSLANGUAGE) === languageCode,
        );
    }, [dataset, languageFilter]);

    const sortedItems = useMemo(
        () => sortCriticalUpdates(filteredItems, sortColumn, sortDirection),
        [filteredItems, sortColumn, sortDirection],
    );

    const isDefaultView =
        !appliedFilters.searchInput.trim() &&
        appliedFilters.statusFilter === 'Active' &&
        appliedFilters.startDate === defaultDateRange.startDate &&
        appliedFilters.endDate === defaultDateRange.endDate &&
        languageFilter === 'All';
    const emptyMessage = isDefaultView
        ? text('NoUpdates', 'No critical organization updates are available.')
        : text(
              'NoResults',
              'No results match the selected search and filters.',
          );
    const searchPlaceholder = text(
        'SearchPlaceholder',
        'Enter keyword to search',
    );

    const handleDeleteRequest = (updateId: string) => {
        setDeleteTargetId(updateId);
        setIsDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTargetId || isDeleteConfirming) return;

        setIsDeleteConfirming(true);
        setIsDeleteDialogOpen(false);
        setIsErrorVisible(false);

        try {
            await new CriticalOrganizationUpdatesService().deleteNews(
                deleteTargetId,
            );
            setSuccessMessage(
                text(
                    'DeleteSuccess',
                    'Critical Organization Updates have been successfully deleted.',
                ),
            );
            setIsSuccessVisible(true);
            window.scrollTo(SCROLL_TOP, SCROLL_TOP);
            void fetchUpdates(appliedFilters);
        } catch {
            setErrorMessage(
                text(
                    'DeleteError',
                    'Unable to delete the critical organization update. Please try again later.',
                ),
            );
            setIsErrorVisible(true);
            window.scrollTo(SCROLL_TOP, SCROLL_TOP);
        } finally {
            setIsDeleteConfirming(false);
            setDeleteTargetId('');
        }
    };

    return (
        <>
            <main className='critical-updates'>
                {successMessage && (
                    <Notification
                        content={successMessage}
                        type={NotificationType.SUCCESS}
                        visibility={isSuccessVisible}
                        setVisibility={setIsSuccessVisible}
                    />
                )}
                {errorMessage && (
                    <Notification
                        content={errorMessage}
                        type={NotificationType.ERROR}
                        visibility={isErrorVisible}
                        setVisibility={setIsErrorVisible}
                    />
                )}
                <div className='page-title'>
                    <h1>
                        {text('PageTitle', 'Critical Organization Updates')}
                    </h1>
                </div>
                <CriticalOrganizationUpdatesTabs active='update' />
                <section className='c-form-section critical-updates__card critical-updates__search'>
                    <div className='c-form-section__content'>
                        <h2>
                            {text(
                                'SearchTitle',
                                'Search Critical Organization Updates',
                            )}
                        </h2>
                        <p className='critical-updates__description'>
                            {text(
                                'SearchDescription',
                                'Search critical organization updates by keyword or refine your results using the filters below.',
                            )}
                        </p>

                        <div className='critical-updates__search-row'>
                            <div className='critical-updates__keyword-row'>
                                <div className='c-input search-text critical-updates__search-input'>
                                    <span id='searchIcon'>
                                        <i
                                            className='c-icon c-icon--[semantic-search]'
                                            aria-hidden='true'
                                            role='img'
                                        />
                                    </span>
                                    <input
                                        aria-label={searchPlaceholder}
                                        className='c-input__input'
                                        placeholder={searchPlaceholder}
                                        value={searchInput}
                                        onChange={(event) =>
                                            setSearchInput(event.target.value)
                                        }
                                        onKeyDown={handleSearchKeyDown}
                                    />
                                    {searchInput && (
                                        <button
                                            className='critical-updates__search-clear'
                                            type='button'
                                            aria-label={text(
                                                'ClearSearch',
                                                'Clear search',
                                            )}
                                            onClick={clearSearchText}
                                        >
                                            <i
                                                className='c-icon c-icon--[semantic-close]'
                                                aria-hidden='true'
                                            />
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className='critical-updates__filter-row'>
                                <label>
                                    <span>{text('StatusLabel', 'Status')}</span>
                                    <ComboBox
                                        key={`status-${filterResetKey}`}
                                        name='critical-update-status'
                                        options={[
                                            statusAllLabel,
                                            statusActiveLabel,
                                            statusExpiredLabel,
                                        ]}
                                        selectedOption={
                                            statusFilter === 'Active'
                                                ? statusActiveLabel
                                                : statusFilter === 'Expired'
                                                ? statusExpiredLabel
                                                : statusAllLabel
                                        }
                                        onSelect={handleStatusSelect}
                                    />
                                </label>

                                <label>
                                    <span>{languageLabel}</span>
                                    <ComboBox
                                        name='critical-update-language'
                                        options={[
                                            statusAllLabel,
                                            languageEnglishLabel,
                                            languageFrenchLabel,
                                        ]}
                                        selectedOption={
                                            languageFilter === 'English'
                                                ? languageEnglishLabel
                                                : languageFilter === 'French'
                                                ? languageFrenchLabel
                                                : statusAllLabel
                                        }
                                        onSelect={handleLanguageSelect}
                                    />
                                </label>

                                <label className='date-picker-container'>
                                    <span>
                                        {text(
                                            'DateRangeLabel',
                                            'Status Date Range',
                                        )}
                                    </span>
                                    <DatePicker
                                        key={`date-range-${filterResetKey}`}
                                        startDate={startDate}
                                        endDate={endDate}
                                        handleOnClose={handleDateRangeClose}
                                        unrestrictedDates
                                        disabled={statusFilter === 'All'}
                                    />
                                </label>

                                <button
                                    className='c-btn c-btn--link'
                                    type='button'
                                    onClick={clearFilters}
                                >
                                    {text('ClearFilters', 'Clear Filters')}
                                </button>
                            </div>
                        </div>

                        <div
                            className='critical-updates__results'
                            aria-busy={isLoading}
                        >
                            {isLoading ? (
                                <div className='critical-updates__loader'>
                                    <Spinner />
                                </div>
                            ) : hasError ? (
                                <div
                                    className='critical-updates__error'
                                    role='alert'
                                >
                                    <span>
                                        {text(
                                            'LoadError',
                                            'Unable to retrieve critical organization updates. Please try again later.',
                                        )}
                                    </span>
                                    <button
                                        className='c-btn c-btn--link'
                                        type='button'
                                        onClick={() => {
                                            void fetchUpdates(appliedFilters);
                                        }}
                                    >
                                        {text('Retry', 'Retry')}
                                    </button>
                                </div>
                            ) : (
                                <>
                                    <div className='critical-updates__table-container'>
                                        <CriticalUpdatesTable
                                            items={
                                                filteredItems.length
                                                    ? displayedContent
                                                    : []
                                            }
                                            sortColumn={sortColumn}
                                            sortDirection={sortDirection}
                                            onSort={handleSort}
                                            onHeadlineClick={
                                                handleHeadlineClick
                                            }
                                            onDeleteRequest={
                                                handleDeleteRequest
                                            }
                                            emptyMessage={
                                                filteredItems.length
                                                    ? undefined
                                                    : emptyMessage
                                            }
                                        />
                                        {Boolean(filteredItems.length) && (
                                            <TableFooter
                                                key={`${sortColumn}-${sortDirection}`}
                                                brand={PAGINATION_BRAND}
                                                content={sortedItems}
                                                setDisplayedContent={
                                                    setDisplayedContent
                                                }
                                                defaultPerPage={10}
                                                perPageOptions={['10', '25']}
                                            />
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </section>
            </main>
            <DialogModal
                titleContent={text(
                    'DeleteModalTitle',
                    'Are you sure you want to delete this critical organization update?',
                )}
                bodyContent={text(
                    'DeleteModalBody',
                    'This action is permanent and cannot be undone.',
                )}
                open={isDeleteDialogOpen}
                cancelBtnLabel={t('Button.Cancel', { defaultValue: 'Cancel' })}
                confirmBtnLabel={text('DeleteConfirm', 'Yes, delete')}
                onCancelHandler={() => setIsDeleteDialogOpen(false)}
                onConfirmHandler={handleDeleteConfirm}
            />
            <div className='critical-updates__privacy-footer'>
                <FooterPrivacy />
            </div>
        </>
    );
};

export default ViewCriticalOrganizationUpdates;
